import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, CalendarDays, Clock, MonitorPlay, Gift, Timer } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const lines = [
  { text: "Take Control of", accent: false },
  { text: "Your Business", accent: false },
  { text: "Cash Flow.", accent: true },
];

const detailRows = [
  { icon: CalendarDays, label: "Date", value: "Thursday 12 November 2026" },
  { icon: Clock, label: "Time", value: "6:00 PM – 7:00 PM" },
  { icon: MonitorPlay, label: "Format", value: "Live online, free to attend" },
  { icon: Gift, label: "Bonus", value: "Cash Flow Forecast Template for attendees" },
];

const WEBINAR_AT = new Date("2026-11-12T18:00:00+11:00").getTime();

function Countdown() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, WEBINAR_AT - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return (
    <div className="mt-5 flex items-center justify-between rounded-xl bg-[#0B2B4B] px-4 py-3" data-testid="webinar-countdown">
      <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-300">
        <Timer className="h-3.5 w-3.5 text-[#38BDF8]" /> Starts in
      </span>
      <span className="font-mono-brand text-sm font-semibold text-white">
        {d}d · {h}h · {m}m
      </span>
    </div>
  );
}

export default function Hero({ onNavigate }) {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section
      id="top"
      ref={sectionRef}
      className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28 grain"
      data-testid="hero-section"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-40 h-[34rem] w-[34rem] rounded-full bg-[#0FA3E8]/10 blur-3xl"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
            className="inline-flex items-center gap-2.5 rounded-full border border-[#0FA3E8]/30 bg-[#F0F9FF] px-4 py-1.5"
            data-testid="hero-eyebrow"
          >
            <span className="h-2 w-2 rounded-full bg-[#0FA3E8] pulse-dot" />
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#0B2B4B]">
              Free 1-hour webinar · Thursday 12 November
            </span>
          </motion.div>

          <h1 className="font-display mt-7 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.04] text-[#0B2B4B]">
            {lines.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1 -mb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.3 + i * 0.13 }}
                >
                  {line.accent ? (
                    <span className="hero-sweep italic text-[#0284C7]">{line.text}</span>
                  ) : (
                    line.text
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.85 }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600"
            data-testid="hero-subcopy"
          >
            No complicated finance talk, just practical cash flow management for
            business owners. Join our free 1-hour Cash Flow Management Webinar on
            Thursday 12 November and leave with a clear picture of your cash flow
            and a plan to stay ahead of it.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.0 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => onNavigate("#register")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-[#0B2B4B] px-7 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white shadow-lg shadow-[#0B2B4B]/20 transition-all duration-200 hover:bg-[#0FA3E8] hover:shadow-[#0FA3E8]/30 active:scale-[0.97]"
              data-testid="hero-register-btn"
            >
              Register my interest
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => onNavigate("#learn")}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-[#0B2B4B] hover:text-[#0FA3E8] transition-colors duration-200"
              data-testid="hero-learn-btn"
            >
              See what you'll learn
              <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" />
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            style={{ y: imgY }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: EASE, delay: 0.5 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-2xl shadow-[#0B2B4B]/10 rotate-2">
              <img
                src="/hero-tradie.webp"
                alt="Smiling small business owner in his workshop"
                className="h-56 sm:h-64 w-full object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 1.2 }}
              className="absolute -bottom-5 -left-4 sm:-left-8 rounded-2xl bg-[#0B2B4B] px-5 py-3.5 shadow-xl -rotate-2"
              data-testid="hero-stat-chip"
            >
              <p className="font-mono-brand text-2xl font-semibold text-[#38BDF8]">~80%</p>
              <p className="text-[11px] font-medium text-slate-300 leading-tight max-w-[12rem]">
                of Australian SMBs have experienced cash flow challenges
                <span className="text-slate-500"> · CommBank, 2025</span>
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            style={{ y: cardY }}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
            className="relative z-10 mt-12 rounded-2xl border border-slate-200/80 bg-white p-6 sm:p-7 shadow-xl shadow-[#0B2B4B]/5"
            data-testid="webinar-details-card"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#0FA3E8]">
              Webinar details
            </p>
            <ul className="mt-4 space-y-4">
              {detailRows.map((row) => (
                <li key={row.label} className="flex items-start gap-3.5">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F0F9FF] text-[#0284C7]">
                    <row.icon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                      {row.label}
                    </p>
                    <p className="text-sm font-semibold text-[#0F172A]">{row.value}</p>
                  </div>
                </li>
              ))}
            </ul>
            <Countdown />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
